import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

interface DatePickerProps {
  selectedMonth: string;
  onMonthChange: (monthValue: string) => void;
  className?: string;
}

const parseSelectedMonth = (selectedMonth: string): { year: number; month: number } => {
  const parts = selectedMonth.split('-');
  if (parts.length !== 2) {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  }
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]);
  return (isNaN(year) || isNaN(month) || month < 1 || month > 12) 
    ? { year: new Date().getFullYear(), month: new Date().getMonth() + 1 }
    : { year, month };
};

export function DatePicker({ 
  selectedMonth, 
  onMonthChange, 
  className = '' 
}: DatePickerProps) {
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [pickerYear, setPickerYear] = useState<number>(() => {
    const { year } = parseSelectedMonth(selectedMonth);
    return year;
  });
  
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const formatMonthYear = (year: number, month: number): string => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    if (year === currentYear && month === currentMonth) {
      return 'This Month';
    } else if (
      (year === currentYear && month === currentMonth - 1) ||
      (currentMonth === 1 && year === currentYear - 1 && month === 12)
    ) {
      return 'Last Month';
    } else {
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  const getCurrentMonthYear = (): string => {
    const { year, month } = parseSelectedMonth(selectedMonth);
    return formatMonthYear(year, month);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const { year, month } = parseSelectedMonth(selectedMonth);
    
    let newYear = year;
    let newMonth = month;
    
    if (direction === 'next') {
      if (month === 12) {
        newYear = year + 1;
        newMonth = 1;
      } else {
        newMonth = month + 1;
      }
    } else {
      if (month === 1) {
        newYear = year - 1;
        newMonth = 12;
      } else {
        newMonth = month - 1;
      }
    }
    
    const monthValue = `${newYear}-${String(newMonth).padStart(2, '0')}`;
    onMonthChange(monthValue);
  };

  const handleDateSelect = (year: number, month: number) => {
    const monthValue = `${year}-${String(month).padStart(2, '0')}`;
    onMonthChange(monthValue);
    setShowDatePicker(false);
  };

  const navigatePickerYear = (direction: 'prev' | 'next') => {
    setPickerYear(prev => direction === 'next' ? prev + 1 : prev - 1);
  };

  const months = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' },
  ];

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center bg-white border border-gray-300 rounded-lg p-1">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-1.5 hover:bg-gray-100 transition-colors rounded-md"
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        
        <button
          ref={buttonRef}
          onClick={() => setShowDatePicker(!showDatePicker)}
          className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 transition-colors rounded-md"
        >
          <Calendar size={16} />
          <span className="font-medium">{getCurrentMonthYear()}</span>
        </button>
        
        <button
          onClick={() => navigateMonth('next')}
          className="p-1.5 hover:bg-gray-100 transition-colors rounded-md"
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {showDatePicker && (
        <div 
          ref={popoverRef}
          className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 min-w-[280px]"
        >
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigatePickerYear('prev')}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="font-semibold text-lg">
              {pickerYear}
            </div>
            <button
              onClick={() => navigatePickerYear('next')}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            {months.map(month => {
              const { year: selectedYear, month: selectedMonthNum } = parseSelectedMonth(selectedMonth);
              const isSelected = pickerYear === selectedYear && 
                               month.value === selectedMonthNum;
              const isCurrent = pickerYear === new Date().getFullYear() && 
                              month.value === new Date().getMonth() + 1;
              
              return (
                <button
                  key={month.value}
                  onClick={() => handleDateSelect(pickerYear, month.value)}
                  className={`p-3 text-sm rounded-lg transition-colors ${
                    isSelected
                      ? 'bg-blue-500 text-white font-medium'
                      : isCurrent
                      ? 'bg-blue-50 text-blue-700 font-medium border border-blue-200'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {month.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}